import { DialogComponent } from '@theme/dialog';
import { StandardEvents } from '@shopify/events';

const selections = new Map();
const FRAME_SELECTION_EVENT = 'frame-selector:change';

/**
 * Returns the leading measurement from a size label.
 * This lets a poster size such as "30 x 40 cm" be compared with a frame width such as "31 cm".
 *
 * @param {string} size
 * @returns {number|null}
 */
function leadingMeasurement(size) {
  const match = String(size).trim().match(/^\d+(?:[.,]\d+)?/);
  if (!match) return null;

  const measurement = Number(match[0].replace(',', '.'));
  return Number.isFinite(measurement) ? measurement : null;
}

class FrameSelector extends DialogComponent {
  requiredRefs = ['dialog', 'triggerLabel', 'selectionSummary', 'selectionText', 'liveRegion', 'sizeLabel', 'emptyState'];
  #abortController = new AbortController();
  #size = '';
  #selected = null;

  connectedCallback() {
    super.connectedCallback();
    this.#size = this.dataset.selectedSize || '';
    this.#selected = selections.get(this.dataset.sectionId || '') || null;
    this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, {
      signal: this.#abortController.signal,
    });
    this.#refreshCards();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #onProductSelect = async (event) => {
    try {
      const { detail } = await event.promise;
      const options = detail?.resource?.options;
      if (!options) return;
      this.#size = options[Number(this.dataset.sizeOptionIndex || 0)] || '';
      this.#refreshCards();
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[frame-selector] Variant update failed:', error);
    }
  };

  #getVariants(card) {
    const script = card.querySelector('script[ref="frameVariants"]');
    try { return JSON.parse(script?.textContent || '[]'); } catch { return []; }
  }

  #variantFor(card) {
    const variants = this.#getVariants(card);
    const exactMatch = variants.find((variant) => variant.size === this.#size);
    if (exactMatch) return exactMatch;

    const selectedMeasurement = leadingMeasurement(this.#size);
    if (selectedMeasurement === null) return undefined;

    const sameWidthMatch = variants.find((variant) => leadingMeasurement(variant.size) === selectedMeasurement);
    if (sameWidthMatch) return sameWidthMatch;

    // Poster rails are one centimetre wider than the print (30 cm poster → 31 cm rail).
    return variants.find((variant) => leadingMeasurement(variant.size) === selectedMeasurement + 1);
  }

  #refreshCards() {
    this.refs.sizeLabel.textContent = this.#size;
    let selectedStillAvailable = false;
    let availableCount = 0;
    for (const card of this.querySelectorAll('.frame-selector__card')) {
      const variant = this.#variantFor(card);
      const available = Boolean(variant?.available);
      if (available) availableCount += 1;
      card.disabled = !available;
      card.querySelector('[ref="framePrice"]').textContent = variant?.price || '—';
      const selected = card.dataset.frameHandle === this.#selected?.handle && available;
      card.setAttribute('aria-pressed', String(selected));
      if (selected) {
        this.#selected = { handle: card.dataset.frameHandle, title: card.dataset.frameTitle, variant };
        selectedStillAvailable = true;
      }
    }
    this.refs.emptyState.hidden = availableCount > 0;
    if (this.#selected && !selectedStillAvailable) this.#clearSelection(false);
    else {
      this.#renderSelection();
      this.#dispatchPriceChange();
    }
  }

  selectFrame(event) {
    const card = event.target.closest('.frame-selector__card');
    if (!card || card.disabled) return;
    const variant = this.#variantFor(card);
    if (!variant) return;
    this.#selected = { handle: card.dataset.frameHandle, title: card.dataset.frameTitle, variant };
    selections.set(this.dataset.sectionId || '', this.#selected);
    this.#refreshCards();
    this.#playSelectionFeedback();
    this.refs.liveRegion.textContent = `${this.#selected.title} selected.`;
    this.closeDialog();
  }

  removeFrame() {
    this.#clearSelection(true);
    this.closeDialog();
  }

  #clearSelection(announce) {
    this.#selected = null;
    selections.delete(this.dataset.sectionId || '');
    for (const card of this.querySelectorAll('.frame-selector__card')) card.setAttribute('aria-pressed', 'false');
    this.#renderSelection();
    this.#dispatchPriceChange();
    if (announce) this.refs.liveRegion.textContent = 'Frame removed.';
  }

  #renderSelection() {
    this.refs.selectionSummary.hidden = !this.#selected;
    const selectedFormat = this.dataset.selectedTextFormat || '[name] · [variant]';
    const selectedText = this.#selected
      ? selectedFormat.replaceAll('[name]', this.#selected.title).replaceAll('[variant]', this.#size)
      : this.dataset.buttonLabel;
    this.refs.triggerLabel.textContent = selectedText;
    this.refs.triggerLabel.closest('button')?.setAttribute('aria-label', selectedText);
    if (this.refs.selectedCheck) this.refs.selectedCheck.hidden = !this.#selected;
    if (this.#selected) this.refs.selectionText.textContent = `${this.#selected.title} · ${this.#selected.variant.price}`;
  }

  #dispatchPriceChange() {
    this.dispatchEvent(
      new CustomEvent(FRAME_SELECTION_EVENT, {
        bubbles: true,
        detail: { priceCents: Number(this.#selected?.variant?.priceCents || 0) },
      })
    );
  }

  #playSelectionFeedback() {
    const button = this.refs.triggerButton;
    if (!button?.hasAttribute('data-selection-feedback')) return;
    button.classList.remove('frame-selector__trigger--feedback');
    void button.offsetWidth;
    button.classList.add('frame-selector__trigger--feedback');
  }

  getSelectedCartItem(quantity = 1) {
    if (!this.#selected?.variant?.available) return null;
    return { variantId: String(this.#selected.variant.id), quantity };
  }
}

if (!customElements.get('frame-selector')) customElements.define('frame-selector', FrameSelector);
